import {AuthRepository} from '../../domain/auth/repositories/AuthRepository';
import {
  User,
  UserCredentials,
  TwoFactorSendCodeRequest,
  TwoFactorSendCodeResponse,
  TwoFactorVerifyResponse,
} from '../../domain/auth/entities/User';
import {AsyncStorageAdapter} from '../storage/AsyncStorageAdapter';
import {sociantApiClient, ApiError} from '../../shared/http/apiClient';

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  role: string;
  preferred_language: string;
}


export class AuthRepositoryImpl implements AuthRepository {
  constructor(private readonly storageAdapter: AsyncStorageAdapter) {}

  async login(credentials: UserCredentials): Promise<User> {
    try {
      // 1. Call login endpoint - this returns a temporary token for 2FA
      const loginResponse = await sociantApiClient.post<LoginResponse>(
        '/api/auth',
        {
          email: credentials.email,
          password: credentials.password,
        },
      );

      const {access_token, refresh_token, role, preferred_language} = loginResponse.data;

      const user: User = {
        id: Date.now().toString(),
        email: credentials.email,
        name: credentials.email.split('@')[0],
        token: access_token,
        refresh_token,
        temp_token: access_token, // Usado para el flujo 2FA
        role,
        preferred_language,
      };

      await this.storageAdapter.saveUser(user);

      return user;
    } catch (error: any) {
      const apiError = error as ApiError;
      const errorMessage =
        apiError.message ||
        apiError.data?.message ||
        'Error de autenticación. Por favor verifica tus credenciales.';
      throw new Error(errorMessage);
    }
  }

  async logout(): Promise<void> {
    await this.storageAdapter.removeUser();
  }

  async getCurrentUser(): Promise<User | null> {
    const storedUser = await this.storageAdapter.getUser();
    if (!storedUser?.token || storedUser.temp_token) {
      // Not fully authenticated — return as-is (may be null or mid-2FA)
      return storedUser;
    }

    try {
      const response = await sociantApiClient.get<any>(
        '/api/user',
        {Authorization: `Bearer ${storedUser.token}`},
      );

      const data = response.data;
      const freshUser: User = {
        ...storedUser,
        id: data.id?.toString() || storedUser.id,
        name: data.name || data.username || storedUser.name,
        email: data.email || storedUser.email,
        role: data.role || storedUser.role,
        preferred_language: data.preferred_language || storedUser.preferred_language,
      };

      await this.storageAdapter.saveUser(freshUser);
      return freshUser;
    } catch {
      // API unavailable or token expired — return cached user
      return storedUser;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    const user = await this.storageAdapter.getUser();
    // User is authenticated if they have a final token (not a temp token)
    // A temp token means they need to complete 2FA
    return user !== null && !!user.token && !user.temp_token;
  }

  async send2FACode(
    request: TwoFactorSendCodeRequest,
    token?: string,
  ): Promise<TwoFactorSendCodeResponse> {
    try {
      // Use provided token or get from storage
      let authToken = token;
      if (!authToken) {
        const user = await this.storageAdapter.getUser();
        authToken = user?.temp_token || user?.token || null;
      }

      if (!authToken) {
        throw new Error('No token available to send 2FA code');
      }

      const response = await sociantApiClient.post<TwoFactorSendCodeResponse>(
        '/api/auth/send-two-factor-code',
        {type: request.type},
        {
          Authorization: `Bearer ${authToken}`,
        },
      );

      return response.data;
    } catch (error: any) {
      const apiError = error as ApiError;
      const errorMessage =
        apiError.message ||
        apiError.data?.message ||
        'Error al enviar el código de verificación.';
      throw new Error(errorMessage);
    }
  }

  async verify2FACode(
    code: string,
    token?: string,
  ): Promise<TwoFactorVerifyResponse> {
    try {
      // Use provided token or get from storage
      let authToken = token;
      if (!authToken) {
        const user = await this.storageAdapter.getUser();
        authToken = user?.temp_token || user?.token || null;
      }

      if (!authToken) {
        throw new Error('No token available to verify 2FA code');
      }

      const response = await sociantApiClient.get<TwoFactorVerifyResponse>(
        `/api/auth/validate-two-factor-code/${code}`,
        {
          Authorization: `Bearer ${authToken}`,
        },
      );

      const {access_token, refresh_token, role, preferred_language} = response.data;

      const storedUser = await this.storageAdapter.getUser();
      const emailPrefix = (storedUser?.email || 'usuario@example.com').split('@')[0];
      const userName = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1);

      const finalUser: User = {
        id: storedUser?.id || Date.now().toString(),
        email: storedUser?.email || 'usuario@example.com',
        name: userName,
        token: access_token,
        refresh_token,
        role,
        preferred_language,
        // temp_token omitido → isAuthenticated = true
      };

      await this.storageAdapter.saveUser(finalUser);

      return {
        access_token,
        refresh_token,
        role,
        preferred_language,
      };
    } catch (error: any) {
      const apiError = error as ApiError;
      const errorMessage =
        apiError.message ||
        apiError.data?.message ||
        'Código de verificación inválido.';
      throw new Error(errorMessage);
    }
  }
}

